/**
 * Firebase 配置檔案 - 單例模式
 * 
 * 使用簡潔的單例模式確保 Firebase 只初始化一次
 * 適用於 Vite HMR 開發環境
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'

/**
 * Firebase 專案配置
 */
const firebaseConfig = {
  apiKey: "AIzaSyCQNVzX6yFBqdVOR3GDd3p9JNLovhTp-ls",
  authDomain: "kikuflow-ai.firebaseapp.com",
  projectId: "kikuflow-ai",
  storageBucket: "kikuflow-ai.firebasestorage.app",
  messagingSenderId: "227669072962",
  appId: "1:227669072962:web:e88ca1f48f7e1deb2a21db",
  measurementId: "G-7FRDZ90WX9"
}

// 全域類型擴展
declare global {
  interface Window {
    __FIREBASE_EMULATORS_CONNECTED__?: boolean
  }
}

/**
 * 單例 Firebase App - 使用標準模式
 * 這是最穩健的方式：簡單、明確、可靠
 */
const app: FirebaseApp = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]

/**
 * Firebase 服務實例 - 每個只初始化一次
 */
export const auth: Auth = getAuth(app)
export const db: Firestore = getFirestore(app)
export const storage: FirebaseStorage = getStorage(app)
export const functions: Functions = getFunctions(app, 'us-west1')

/**
 * 開發環境判斷
 */
const hostname: string = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
const isDev: boolean = (
  hostname === 'localhost' || 
  hostname === '127.0.0.1' || 
  hostname.startsWith('192.168.') ||
  hostname.endsWith('.local')
)

/**
 * 模擬器連接 - 使用全局標記確保只連接一次
 */
const connectEmulators = (): void => {
  // 全局標記，防止重複連接
  if (typeof window !== 'undefined' && window.__FIREBASE_EMULATORS_CONNECTED__) {
    return
  }

  if (typeof window !== 'undefined' && isDev) {
    console.log(`🔧 開發環境檢測: ${hostname} (isDev: ${isDev})`)
    
    try {
      connectAuthEmulator(auth, `http://${hostname}:9099`, { disableWarnings: true })
      console.log(`🔧 Auth Emulator connected to ${hostname}:9099`)
    } catch (e) {
      console.warn('Auth Emulator 連接失敗:', e)
    }

    try {
      connectFirestoreEmulator(db, hostname, 8080)
      console.log(`🔧 Firestore Emulator connected to ${hostname}:8080`)
    } catch (e) {
      console.warn('Firestore Emulator 連接失敗:', e)
    }

    try {
      connectStorageEmulator(storage, hostname, 9199)
      console.log(`🔧 Storage Emulator connected to ${hostname}:9199`)
    } catch (e) {
      console.warn('Storage Emulator 連接失敗:', e)
    }

    try {
      connectFunctionsEmulator(functions, hostname, 5001)
      console.log(`🔧 Functions Emulator connected to ${hostname}:5001`)
    } catch (e) {
      console.warn('Functions Emulator 連接失敗:', e)
    }

    // 設定全局標記
    if (typeof window !== 'undefined') {
      window.__FIREBASE_EMULATORS_CONNECTED__ = true
    }
  }
}

// 立即嘗試連接模擬器
connectEmulators()

export default app